
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PasswordInput } from "./components/PasswordInput";
import { TermsCheckbox } from "./components/TermsCheckbox";
import { AlertMessage } from "./components/AlertMessage";

export const SignupForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showVerificationAlert, setShowVerificationAlert] = useState(false);
  const [showExistingAccountAlert, setShowExistingAccountAlert] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(10);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!email || !password) {
        throw new Error("Please fill in all fields");
      }

      if (!acceptedTerms) {
        throw new Error("Please accept the Terms of Service and Privacy Policy");
      }

      console.log("Attempting to sign up with email:", email.trim());
      
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password: password.trim(),
      });

      if (error) {
        console.error("Supabase signup error:", error);
        
        if (error.message.includes("User already registered")) {
          console.log("User already registered, showing alert");
          setShowExistingAccountAlert(true);
          
          const countdownInterval = setInterval(() => {
            setRedirectCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                navigate("/login");
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          return;
        }

        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      if (data.user && !error) {
        console.log("Signup successful, user created:", data.user.id);
        setShowVerificationAlert(true);
        
        // Log any email confirmation metadata if available
        if (data.user.confirmation_sent_at) {
          console.log("Confirmation email sent at:", data.user.confirmation_sent_at);
        }
        
        toast({
          title: "Account created successfully!",
          description: "Please check your email to verify your account.",
        });

        try {
          // Additional notification to help debug Resend issues
          console.log("Attempting to send additional notification email");
          const notificationResult = await supabase.functions.invoke('send-notification-email', {
            body: {
              userId: data.user.id,
              type: 'signup_welcome',
              data: {
                email: email.trim()
              }
            }
          });
          
          console.log("Additional notification email result:", notificationResult);
        } catch (emailError) {
          console.error("Failed to send additional notification:", emailError);
          // Non-blocking error, don't interrupt the signup flow
        }

        const countdownInterval = setInterval(() => {
          setRedirectCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              navigate("/login");
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }

    } catch (error) {
      console.error("Signup error:", error);
      toast({
        title: "Error",
        description: error.message || "An error occurred while creating your account",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {showExistingAccountAlert && (
        <AlertMessage 
          type="existing"
          email={email}
          countdown={redirectCountdown}
        />
      )}

      {showVerificationAlert && (
        <AlertMessage 
          type="verification"
          email={email}
          countdown={redirectCountdown}
        />
      )}

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium">
          Email
        </label>
        <Input
          id="email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          spellCheck={false}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="signup-password" className="text-sm font-medium">
          Password
        </label>
        <PasswordInput 
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <p className="text-xs text-gray-500">
          Password must be at least 6 characters long
        </p>
      </div>

      <TermsCheckbox 
        checked={acceptedTerms}
        onCheckedChange={setAcceptedTerms}
      />

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Please wait..." : "Create Account"}
      </Button>
    </form>
  );
};
